import {
  useRef,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react"
import {
  PlayIcon,
  StopIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/24/outline"
import { ArrowPathIcon } from "@heroicons/react/24/solid"
import type { ScrapedImage } from "../types"

// Per-slide motion direction. Omitting it preserves the legacy default
// behavior (left-to-right pan + slight zoom-in), so existing AdForge
// callers don't need to opt in.
export type SlideMotion = "ltr" | "rtl" | "ttb" | "btt" | "zoom-in" | "zoom-out"

interface KenBurnsPlayerProps {
  images: ScrapedImage[]
  aspectRatio: "1:1" | "9:16"
  /** Shared duration for every slide. Overridden by `durations[i]` when set. */
  durationPerImage?: number
  /** Per-slide duration override (ms). Length should match `images.length`. */
  durations?: number[]
  /** Per-slide motion direction. Defaults to "ltr" for every slide. */
  motions?: SlideMotion[]
  zoomIntensity?: number
  velocity?: number
  logoUrl?: string
  includeEndcard?: boolean
  proxyImageUrl?: (url: string) => Promise<string>
  proxyVideoUrl?: (url: string) => Promise<string>
  children?: React.ReactNode
}

export interface KenBurnsRef {
  generateVideo: () => Promise<string | null>
}

interface LoadedAsset {
  id: string
  type: "image" | "video" | "gif"
  element: HTMLImageElement | HTMLVideoElement
  duration: number
}

const KenBurnsPlayer = forwardRef<KenBurnsRef, KenBurnsPlayerProps>(
  (
    {
      images,
      aspectRatio,
      durationPerImage = 2500,
      durations,
      motions,
      zoomIntensity = 0.15,
      velocity = 0.5,
      logoUrl,
      includeEndcard,
      proxyImageUrl,
      proxyVideoUrl,
      children,
    },
    ref,
  ) => {
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const [isPlaying, setIsPlaying] = useState(false)
    const [isGenerating, setIsGenerating] = useState(false)
    const [loadError, setLoadError] = useState<string | null>(null)
    const [assetsReady, setAssetsReady] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [loadProgress, setLoadProgress] = useState(0)
    const animationRef = useRef<number | undefined>(undefined)

    // Use lower resolution for preview; full res only for generation
    const previewScale = 0.5
    const fullWidth = 1080
    const fullHeight = aspectRatio === "1:1" ? 1080 : 1920
    const width = Math.round(fullWidth * previewScale)
    const height = Math.round(fullHeight * previewScale)

    const loadedAssetsRef = useRef<LoadedAsset[]>([])
    const logoImageRef = useRef<HTMLImageElement | null>(null)
    const proxyVideoUrlRef = useRef(proxyVideoUrl)
    proxyVideoUrlRef.current = proxyVideoUrl
    const proxyImageUrlRef = useRef(proxyImageUrl)
    proxyImageUrlRef.current = proxyImageUrl
    // Per-slide overrides — kept in refs so the rAF loop can read latest
    // values without restarting (don't want to rebuild the asset graph
    // every time the user nudges a motion select / duration slider).
    const motionsRef = useRef(motions)
    motionsRef.current = motions
    const perSlideDurationsRef = useRef(durations)
    perSlideDurationsRef.current = durations
    const durationRef = useRef(durationPerImage)
    durationRef.current = durationPerImage

    const drawFrame = (time: number) => {
      const canvas = canvasRef.current
      if (!canvas || loadedAssetsRef.current.length === 0) return
      const ctx = canvas.getContext("2d", { alpha: false })
      if (!ctx) return

      // Effective per-slide duration: prefer the live `durations` array
      // when present, otherwise fall back to whatever each LoadedAsset was
      // built with. Read fresh every frame so slider tweaks update the
      // timeline without rebuilding loaded assets.
      const effectiveDuration = (i: number) =>
        perSlideDurationsRef.current?.[i] ?? loadedAssetsRef.current[i].duration
      const totalAssetsDuration = loadedAssetsRef.current.reduce(
        (acc, _, i) => acc + effectiveDuration(i),
        0,
      )
      const endcardDuration = includeEndcard && logoUrl ? 2000 : 0
      const totalDuration = totalAssetsDuration + endcardDuration
      const loopedTime = time % totalDuration

      if (loopedTime >= totalAssetsDuration && endcardDuration > 0) {
        ctx.fillStyle = "#FFFFFF"
        ctx.fillRect(0, 0, width, height)
        if (logoImageRef.current) {
          const logo = logoImageRef.current
          const progress = (loopedTime - totalAssetsDuration) / endcardDuration
          const scale = Math.min(
            (width * 0.6) / logo.width,
            (height * 0.4) / logo.height,
          )
          ctx.globalAlpha = Math.min(progress * 4, 1)
          ctx.drawImage(
            logo,
            (width - logo.width * scale) / 2,
            (height - logo.height * scale) / 2,
            logo.width * scale,
            logo.height * scale,
          )
          ctx.globalAlpha = 1
        }
        return
      }

      let cumulative = 0
      let current: {
        asset: LoadedAsset
        progress: number
        localTime: number
        index: number
      } | null = null
      for (let i = 0; i < loadedAssetsRef.current.length; i++) {
        const asset = loadedAssetsRef.current[i]
        const dur = effectiveDuration(i)
        if (loopedTime >= cumulative && loopedTime < cumulative + dur) {
          current = {
            asset,
            progress: (loopedTime - cumulative) / dur,
            localTime: loopedTime - cumulative,
            index: i,
          }
          break
        }
        cumulative += dur
      }
      if (!current) return

      const { asset, progress, localTime, index: slideIndex } = current
      const slideMotion: SlideMotion = motionsRef.current?.[slideIndex] ?? "ltr"

      if (asset.type === "video") {
        const video = asset.element as HTMLVideoElement

        if (!isNaN(video.duration) && video.duration > 0) {
          const targetSec = (localTime / 1000) % video.duration
          if (Math.abs(video.currentTime - targetSec) > 0.1) {
            video.currentTime = targetSec
          }
        }

        const vW = video.videoWidth
        const vH = video.videoHeight
        if (vW > 0 && vH > 0) {
          const vRatio = vW / vH
          const cRatio = width / height
          let dW
          let dH
          let dX
          let dY
          if (vRatio > cRatio) {
            dH = height
            dW = height * vRatio
            dY = 0
            dX = (width - dW) / 2
          } else {
            dW = width
            dH = width / vRatio
            dX = 0
            dY = (height - dH) / 2
          }
          ctx.drawImage(video, dX, dY, dW, dH)
        } else {
          ctx.fillStyle = "#000000"
          ctx.fillRect(0, 0, width, height)
        }
      } else {
        const img = asset.element as HTMLImageElement
        const iRatio = img.width / img.height
        const cRatio = width / height

        let baseScale
        if (iRatio > cRatio) {
          baseScale = height / img.height
        } else {
          baseScale = width / img.width
        }

        // Resolve the per-slide Ken Burns motion into a scale + pan
        // contribution. Pan motions add a subtle zoom so the camera always
        // feels alive; pure zoom motions skip the pan entirely.
        const PAN_INTENSITY = 0.3
        const PAN_ZOOM = 1 + progress * zoomIntensity * velocity
        const ZOOM_INTENSE = 1 + progress * zoomIntensity * velocity * 2.2
        const ZOOM_OUT_FROM = 1 + zoomIntensity * velocity * 2.2 // starting scale for zoom-out
        let scaleMultiplier = PAN_ZOOM
        let panFactorX = 0
        let panFactorY = 0
        switch (slideMotion) {
          case "ltr":
            panFactorX = progress
            break
          case "rtl":
            panFactorX = -progress
            break
          case "ttb":
            panFactorY = progress
            break
          case "btt":
            panFactorY = -progress
            break
          case "zoom-in":
            scaleMultiplier = ZOOM_INTENSE
            break
          case "zoom-out":
            scaleMultiplier = ZOOM_OUT_FROM - progress * (ZOOM_OUT_FROM - 1)
            break
        }
        const scale = baseScale * scaleMultiplier
        const scaledW = img.width * scale
        const scaledH = img.height * scale
        const offsetX = (width - scaledW) / 2
        const offsetY = (height - scaledH) / 2
        const panX = panFactorX * (scaledW - width) * PAN_INTENSITY
        const panY = panFactorY * (scaledH - height) * PAN_INTENSITY

        ctx.drawImage(img, offsetX - panX, offsetY - panY, scaledW, scaledH)
      }
    }

    const pauseAllVideos = () => {
      for (const asset of loadedAssetsRef.current) {
        if (asset.type === "video") {
          const video = asset.element as HTMLVideoElement
          video.pause()
        }
      }
    }

    const playAllVideos = () => {
      for (const asset of loadedAssetsRef.current) {
        if (asset.type === "video") {
          void (asset.element as HTMLVideoElement).play()
        }
      }
    }

    const stopPlayback = () => {
      setIsPlaying(false)
      if (animationRef.current) cancelAnimationFrame(animationRef.current)
      animationRef.current = undefined
      pauseAllVideos()
      // Reset video positions
      for (const asset of loadedAssetsRef.current) {
        if (asset.type === "video") {
          const video = asset.element as HTMLVideoElement
          video.currentTime = 0
        }
      }
      drawFrame(0)
    }

    useEffect(() => {
      const loadAssets = async () => {
        setLoadError(null)
        setIsLoading(true)
        setLoadProgress(0)
        let loaded = 0
        const total = images.length
        const promises = images.map(async asset => {
          const result = await ((): Promise<LoadedAsset | null> => {
            if (asset.type === "video") {
              // No crossOrigin for preview — avoids CORS failures
              // crossOrigin is only needed during generateVideo export
              return new Promise<LoadedAsset | null>(resolve => {
                const video = document.createElement("video")
                video.muted = true
                video.playsInline = true
                video.preload = "auto"

                const timeout = setTimeout(() => {
                  resolve(null)
                }, 15000)

                video.oncanplay = () => {
                  clearTimeout(timeout)
                  video.play().catch(() => undefined)
                  resolve({
                    id: asset.id,
                    type: "video",
                    element: video,
                    duration: durationRef.current,
                  })
                }
                video.onerror = () => {
                  clearTimeout(timeout)
                  resolve(null)
                }
                video.src = asset.url
                video.load()
              })
            }
            return new Promise<LoadedAsset | null>(resolve => {
              const image = new Image()
              image.onload = () =>
                resolve({
                  id: asset.id,
                  type: asset.type,
                  element: image,
                  duration: durationRef.current,
                })
              image.onerror = () => resolve(null)
              image.src = asset.url
            })
          })()
          loaded += 1
          setLoadProgress(Math.round((loaded / total) * 100))
          return result
        })

        if (logoUrl) {
          const logoImg = new Image()
          logoImg.crossOrigin = "anonymous"
          logoImg.src = logoUrl
          logoImg.onload = () => {
            logoImageRef.current = logoImg
          }
        }

        try {
          const results = await Promise.all(promises)
          loadedAssetsRef.current = results.filter(
            (a): a is LoadedAsset => a !== null,
          )
          setAssetsReady(loadedAssetsRef.current.length > 0)
          setIsLoading(false)
          if (loadedAssetsRef.current.length === 0 && images.length > 0) {
            setLoadError("Media could not be loaded.")
          } else {
            drawFrame(0)
          }
        } catch {
          setIsLoading(false)
          setLoadError("Critical load error.")
        }
      }

      stopPlayback()
      if (images.length > 0) {
        void loadAssets()
      } else {
        loadedAssetsRef.current = []
        setAssetsReady(false)
        drawFrame(0)
      }
    }, [images, logoUrl])

    useEffect(() => {
      if (loadedAssetsRef.current.length > 0) {
        loadedAssetsRef.current.forEach(asset => {
          asset.duration = durationPerImage
        })
        if (!isPlaying) drawFrame(0)
      }
    }, [durationPerImage, isPlaying])

    const startPlayback = () => {
      if (loadedAssetsRef.current.length === 0) return
      setIsPlaying(true)
      playAllVideos()
      const startTime = performance.now()
      const animate = (t: number) => {
        drawFrame(t - startTime)
        animationRef.current = requestAnimationFrame(animate)
      }
      animationRef.current = requestAnimationFrame(animate)
    }

    // Draw a single frame onto an arbitrary canvas context. Mirrors the
    // preview's drawFrame exactly so the high-res export matches what the
    // user sees in the player (per-slide motion + per-slide duration).
    const drawFrameOnCtx = (
      ctx: CanvasRenderingContext2D,
      w: number,
      h: number,
      time: number,
      assets: LoadedAsset[],
    ) => {
      const totalAssetsDuration = assets.reduce((acc, a) => acc + a.duration, 0)
      const endcardDuration = includeEndcard && logoUrl ? 2000 : 0
      const totalDuration = totalAssetsDuration + endcardDuration
      const loopedTime = time % totalDuration

      if (loopedTime >= totalAssetsDuration && endcardDuration > 0) {
        ctx.fillStyle = "#FFFFFF"
        ctx.fillRect(0, 0, w, h)
        if (logoImageRef.current) {
          const logo = logoImageRef.current
          const progress = (loopedTime - totalAssetsDuration) / endcardDuration
          const scale = Math.min(
            (w * 0.6) / logo.width,
            (h * 0.4) / logo.height,
          )
          ctx.globalAlpha = Math.min(progress * 4, 1)
          ctx.drawImage(
            logo,
            (w - logo.width * scale) / 2,
            (h - logo.height * scale) / 2,
            logo.width * scale,
            logo.height * scale,
          )
          ctx.globalAlpha = 1
        }
        return
      }

      let cumulative = 0
      let current: {
        asset: LoadedAsset
        progress: number
        localTime: number
        index: number
      } | null = null
      for (let i = 0; i < assets.length; i++) {
        const asset = assets[i]
        if (
          loopedTime >= cumulative &&
          loopedTime < cumulative + asset.duration
        ) {
          current = {
            asset,
            progress: (loopedTime - cumulative) / asset.duration,
            localTime: loopedTime - cumulative,
            index: i,
          }
          break
        }
        cumulative += asset.duration
      }
      if (!current) return

      const { asset, progress, localTime, index: slideIndex } = current
      const slideMotion: SlideMotion = motionsRef.current?.[slideIndex] ?? "ltr"

      if (asset.type === "video") {
        const video = asset.element as HTMLVideoElement
        if (!isNaN(video.duration) && video.duration > 0) {
          const targetSec = (localTime / 1000) % video.duration
          if (Math.abs(video.currentTime - targetSec) > 0.1) {
            video.currentTime = targetSec
          }
        }
        const vW = video.videoWidth
        const vH = video.videoHeight
        if (vW > 0 && vH > 0) {
          const vRatio = vW / vH
          const cRatio = w / h
          let dW
          let dH
          let dX
          let dY
          if (vRatio > cRatio) {
            dH = h
            dW = h * vRatio
            dY = 0
            dX = (w - dW) / 2
          } else {
            dW = w
            dH = w / vRatio
            dX = 0
            dY = (h - dH) / 2
          }
          ctx.drawImage(video, dX, dY, dW, dH)
        } else {
          ctx.fillStyle = "#000000"
          ctx.fillRect(0, 0, w, h)
        }
      } else {
        const img = asset.element as HTMLImageElement
        const iRatio = img.width / img.height
        const cRatio = w / h
        let baseScale
        if (iRatio > cRatio) {
          baseScale = h / img.height
        } else {
          baseScale = w / img.width
        }

        const PAN_INTENSITY = 0.3
        const PAN_ZOOM = 1 + progress * zoomIntensity * velocity
        const ZOOM_INTENSE = 1 + progress * zoomIntensity * velocity * 2.2
        const ZOOM_OUT_FROM = 1 + zoomIntensity * velocity * 2.2
        let scaleMultiplier = PAN_ZOOM
        let panFactorX = 0
        let panFactorY = 0
        switch (slideMotion) {
          case "ltr":
            panFactorX = progress
            break
          case "rtl":
            panFactorX = -progress
            break
          case "ttb":
            panFactorY = progress
            break
          case "btt":
            panFactorY = -progress
            break
          case "zoom-in":
            scaleMultiplier = ZOOM_INTENSE
            break
          case "zoom-out":
            scaleMultiplier = ZOOM_OUT_FROM - progress * (ZOOM_OUT_FROM - 1)
            break
        }
        const scale = baseScale * scaleMultiplier
        const scaledW = img.width * scale
        const scaledH = img.height * scale
        const offsetX = (w - scaledW) / 2
        const offsetY = (h - scaledH) / 2
        const panX = panFactorX * (scaledW - w) * PAN_INTENSITY
        const panY = panFactorY * (scaledH - h) * PAN_INTENSITY
        ctx.drawImage(img, offsetX - panX, offsetY - panY, scaledW, scaledH)
      }
    }

    useImperativeHandle(ref, () => ({
      generateVideo: async () => {
        setIsGenerating(true)
        try {
          // Re-load images through proxy as clean data URLs. Each clone
          // also adopts the latest per-slide duration override so the
          // exported video stays in sync with whatever the user just
          // dragged on the duration sliders.
          const cleanAssets: LoadedAsset[] = []
          for (const asset of loadedAssetsRef.current) {
            if (asset.type === "video") {
              if (proxyVideoUrl) {
                const src = (asset.element as HTMLVideoElement).src
                if (!src.startsWith("blob:")) {
                  try {
                    const proxiedSrc = await proxyVideoUrl(src)
                    const video = document.createElement("video")
                    video.crossOrigin = "anonymous"
                    video.muted = true
                    video.playsInline = true
                    video.preload = "auto"
                    await new Promise<void>((res, rej) => {
                      video.oncanplay = () => res()
                      video.onerror = () => rej(new Error("Failed to load"))
                      video.src = proxiedSrc
                      video.load()
                    })
                    cleanAssets.push({ ...asset, element: video })
                  } catch {
                    cleanAssets.push(asset)
                  }
                } else {
                  cleanAssets.push(asset)
                }
              } else {
                cleanAssets.push(asset)
              }
            } else {
              const src = (asset.element as HTMLImageElement).src
              if (
                proxyImageUrl &&
                !src.startsWith("data:") &&
                !src.startsWith("blob:")
              ) {
                const proxiedSrc = await proxyImageUrl(src)
                const img = new Image()
                await new Promise<void>((resolve, reject) => {
                  img.onload = () => resolve()
                  img.onerror = () => reject(new Error("Failed to load"))
                  img.src = proxiedSrc
                })
                cleanAssets.push({ ...asset, element: img })
              } else {
                cleanAssets.push(asset)
              }
            }
          }

          // Apply per-slide duration overrides to the clones so the
          // recorder loop and drawFrameOnCtx pull the same numbers as the
          // preview did.
          for (let i = 0; i < cleanAssets.length; i++) {
            const override = perSlideDurationsRef.current?.[i]
            if (override != null) cleanAssets[i].duration = override
          }

          // Create a fresh canvas at full resolution
          const tempCanvas = document.createElement("canvas")
          tempCanvas.width = fullWidth
          tempCanvas.height = fullHeight
          const tempCtx = tempCanvas.getContext("2d", { alpha: false })
          if (!tempCtx) {
            setIsGenerating(false)
            return null
          }

          drawFrameOnCtx(tempCtx, fullWidth, fullHeight, 0, cleanAssets)

          const stream = tempCanvas.captureStream(30)
          const recorder = new MediaRecorder(stream, {
            mimeType: MediaRecorder.isTypeSupported("video/mp4;codecs=avc1")
              ? "video/mp4;codecs=avc1"
              : "video/webm",
            videoBitsPerSecond: 8000000,
          })
          const chunks: Blob[] = []
          recorder.ondataavailable = e => chunks.push(e.data)

          return new Promise<string | null>(resolve => {
            recorder.onstop = () => {
              setIsGenerating(false)
              resolve(
                URL.createObjectURL(
                  new Blob(chunks, { type: recorder.mimeType }),
                ),
              )
            }
            recorder.start()
            const totalDuration =
              cleanAssets.reduce((acc, a) => acc + a.duration, 0) +
              (includeEndcard && logoUrl ? 2000 : 0)
            const startTime = performance.now()
            const loop = () => {
              const elapsed = performance.now() - startTime
              if (elapsed < totalDuration) {
                drawFrameOnCtx(
                  tempCtx,
                  fullWidth,
                  fullHeight,
                  elapsed,
                  cleanAssets,
                )
                requestAnimationFrame(loop)
              } else {
                drawFrameOnCtx(
                  tempCtx,
                  fullWidth,
                  fullHeight,
                  totalDuration - 1,
                  cleanAssets,
                )
                recorder.stop()
              }
            }
            requestAnimationFrame(loop)
          })
        } catch (err) {
          setIsGenerating(false)
          throw err
        }
      },
    }))

    return (
      <div className="flex flex-col gap-4 w-full">
        <div
          className="relative rounded-2xl overflow-hidden bg-black border border-system-gray-700 shadow-2xl mx-auto ring-4 ring-system-gray-900"
          style={{
            width: "100%",
            maxWidth: aspectRatio === "1:1" ? "400px" : "225px",
          }}
        >
          <canvas
            ref={canvasRef}
            width={width}
            height={height}
            className="w-full h-auto block"
          />
          {isLoading && (
            <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center z-20">
              <p className="text-xs text-white/60 mb-3 font-medium">
                Loading assets... {loadProgress}%
              </p>
              <div className="w-3/4 h-1.5 bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-brand rounded-full transition-all duration-300"
                  style={{ width: `${loadProgress}%` }}
                />
              </div>
            </div>
          )}
          {isGenerating && (
            <div className="absolute inset-0 bg-black/90 flex flex-col items-center justify-center z-20 text-brand">
              <ArrowPathIcon className="w-12 h-12 animate-spin mb-4" />
              <p className="font-black text-xs uppercase tracking-widest text-center px-4">
                Baking 1080p Video Assets...
              </p>
            </div>
          )}
          {loadError && (
            <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center text-error font-bold p-4 text-center">
              <ExclamationCircleIcon className="w-8 h-8 mb-2" />
              {loadError}
            </div>
          )}
        </div>
        <div className="flex gap-2 w-full max-w-[400px] mx-auto">
          <button
            onClick={isPlaying ? stopPlayback : startPlayback}
            className={`btn ${isPlaying ? "btn-error" : "btn-primary"} flex-1 font-black uppercase text-sm h-12`}
            disabled={!assetsReady}
          >
            {isPlaying ? (
              <StopIcon className="w-4 h-4 mr-2" />
            ) : (
              <PlayIcon className="w-4 h-4 mr-2" />
            )}{" "}
            {isPlaying ? "Stop" : "Preview"}
          </button>
          {children}
        </div>
      </div>
    )
  },
)

KenBurnsPlayer.displayName = "KenBurnsPlayer"

export default KenBurnsPlayer
